import React, { useState } from "react";
import { TextDescription, TextTitle } from "components/Texts";

import Column from "components/Column";
import DateFormatter from "../../modules/utils/DateFormatter";
import Dropdown from "components/Dropdown";
import { Line } from "react-chartjs-2";
import Row from "components/Row";
import Section from "components/Section";
import _forEach from "lodash/forEach";
import colors from "styles/colors";
import formatMetricValue from "../../modules/utils/formatMetricValue";
import formatNumber from "../../modules/utils/formatNumber";
import styled from "styled-components";
import trendingToolTip from "./TrendingToolTip";
import { useTranslation } from "react-i18next";

const Trending = ({ dataList, view }) => {
  const [t] = useTranslation();
  const [tooltip, setTooltip] = useState();
  const [metric, setMetric] = useState("cost");

  if (!dataList.length) {
    return null;
  }

  const dropdownItems = [
    {
      title: t("__cost"),
      key: "cost",
    },
    {
      title: t("__clicks"),
      key: "click",
    },
    {
      title: t("__impressions"),
      key: "impression",
    },
    {
      title: t("__ctr"),
      key: "ctr",
    },
    {
      title: t("__cpc"),
      key: "cpc",
    },
    {
      title: t("__conversion"),
      key: "conversion",
    },
  ];
  const onDropdownSelected = (item) => {
    setMetric(item.value);
    setMetric(item.key);
  };

  let formatDate = DateFormatter.formatDateOnly;
  if (view.value === "weekly") {
    formatDate = DateFormatter.formatDateMonth;
  } else if (view.value === "monthly") {
    formatDate = DateFormatter.formatMonthYear;
  } else if (dataList[0].startDate.split("-")[0] !== dataList[dataList.length - 1].startDate.split("-")[0]) {
    formatDate = DateFormatter.formatDateFull;
  } else if (dataList[0].startDate.split("-")[1] !== dataList[dataList.length - 1].startDate.split("-")[1]) {
    formatDate = DateFormatter.formatDateMonth;
  }
  const chartDataList = [];
  _forEach(dataList, function (data) {
    const number = data.data[metric] || 0;
    chartDataList.push({
      value: number < 0.1 ? number.toPrecision(1) : number,
      label:
        view.value === "weekly"
          ? `${formatDate(data.startDate)} - ${formatDate(data.endDate)}`
          : formatDate(data.startDate),
    });
  });

  //console.log("Trending", view, metric, dataList, chartDataList);

  return (
    <Section margin="0 0 24px">
      <Row>
        <Column>
          <TextTitle>{t("__trending")}</TextTitle>
          <TextDescription>{view.title}</TextDescription>
        </Column>
        <Column wrap>
          <Dropdown items={dropdownItems} minWidth="130px" onSelected={onDropdownSelected} />
        </Column>
      </Row>
      <BarZone>
        <TrendingGraph setTooltip={setTooltip} dataList={chartDataList} metric={metric} />
        {tooltip}
      </BarZone>
    </Section>
  );
};

export default Trending;

const TrendingGraph = React.memo(({ setTooltip, dataList, metric }) => {
  const values = dataList.map((item) => item.value);
  const maxVal = Math.max(...values);
  const stepSize = maxVal < 1 ? maxVal / 4 : parseInt(maxVal / 4);

  const data = (canvas) => {
    const ctx = canvas.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
    gradient.addColorStop(0, colors.trendingFull);
    gradient.addColorStop(1, colors.trendingBlank);
    return {
      labels: dataList.map((item) => item.label),
      datasets: [
        {
          label: "",
          fill: true,
          lineTension: 0.1,
          backgroundColor: gradient,
          borderWidth: 2,
          borderColor: colors.primary,
          borderCapStyle: "butt",
          borderDash: [],
          borderDashOffset: 0.0,
          borderJoinStyle: "miter",
          pointRadius: 0,
          pointHitRadius: 10,
          data: values,
        },
      ],
      tooltips: {
        enabled: false,
      },
    };
  };
  return (
    <Line
      data={data}
      options={{
        layout: {
          padding: {
            top: 40,
          },
        },
        tooltips: {
          xPadding: 0,
          yPadding: 0,
          enabled: false,
          custom: (tooltipModel) => {
            trendingToolTip(tooltipModel, setTooltip, metric);
          },
        },
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
              ticks: {
                fontColor: colors.primary,
                fontSize: 10,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                drawBorder: false,
                color: colors.sectionLine,
                zeroLineColor: colors.sectionLine,
              },
              ticks: {
                stepSize: stepSize,
                fontColor: colors.primary,
                callback: (value) =>
                  metric === "ctr" && maxVal < 1 ? formatNumber(value, 3) : formatMetricValue(metric, value),
                fontSize: 10,
              },
            },
          ],
        },
        maintainAspectRatio: false,
      }}
    />
  );
});

const BarZone = styled.div`
  height: 240px;
  position: relative;
`;
